import React, { useState } from "react";
import api from "../../../api/axiosConfig";
import { showToast } from "../../Shared/Toaster";

export interface AdditionalDegree {
  id?: string;
  degreeQualification?: string;
  customQualification?: string;
  practiceCity?: string;
  smcRegistrationId?: string;
  nmcRegistrationId?: string;
}

export interface DocumentItem {
  id?: string;
  name?: string;
  originalFileName?: string;
  size?: number;
  mimeType?: string;
  s3Key?: string;
  url?: string;
  uploadedAt?: string;
  status?: string;
}

export interface DoctorApplicationData {
  _id: string;
  email?: string;
  name?: string;
  doctorName?: string;
  doctorEmail?: string;
  auth0Id?: string;
  mobileNumber?: string;
  isMobileVerified?: boolean;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  mbbsType?: string;
  institutionName?: string;
  degreeQualification?: string;
  doctorType?: string;
  specialization?: string;
  customQualification?: string;
  practiceCity?: string;
  city?: string;
  smcRegistrationId?: string;
  nmcRegistrationId?: string;
  accountStatus?: string;
  adminNotes?: string;
  notes?: string;
  adminFeedback?: string;
  status?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  additionalDegreesList?: AdditionalDegree[];
  documentsList?: DocumentItem[];
  parentName?: string;
  parentEmail?: string;
}

interface Props {
  application: DoctorApplicationData;
  onClose: () => void;
  onSuccess: () => void;
}

const ViewDoctorApplicationModal: React.FC<Props> = ({
  application,
  onClose,
  onSuccess,
}) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState(
    "Please upload clear copy of State Medical Council certificate"
  );
  const [requestReupload, setRequestReupload] = useState(true);

  const fullName =
    application.name ||
    `${application.firstName || ""} ${application.lastName || ""}`.trim() ||
    application.doctorName ||
    application.email ||
    "—";

  const email = application.email || application.doctorEmail || "—";
  const status = application.status || application.accountStatus || "UNKNOWN";

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return "0 KB";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 🔹 Approve Handler
  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const payload = {
        role: "Referral_doctor",
        status: "ACTIVE",
        notes: "All submitted documents verified successfully.",
      };

      await api.Referal_doc.post(
        `/api/admin/doctor-applications/${application._id}/approve`,
        payload,
        { headers }
      );

      showToast("Doctor application approved successfully! ✅", "success");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error approving doctor application:", err);
      showToast(
        err.response?.data?.message || "Failed to approve application ❌",
        "error"
      );
    } finally {
      setIsApproving(false);
    }
  };

  // 🔹 Reject Handler
  const handleRejectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectFeedback.trim()) {
      showToast("Please enter a reason for rejection", "error");
      return;
    }

    setIsRejecting(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const payload = {
        feedback: rejectFeedback.trim(),
        requestReupload,
      };

      await api.Referal_doc.post(
        `/api/admin/doctor-applications/${application._id}/reject`,
        payload,
        { headers }
      );

      showToast("Doctor application rejected successfully ❌", "success");
      setShowRejectModal(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error rejecting doctor application:", err);
      showToast(
        err.response?.data?.message || "Failed to reject application ❌",
        "error"
      );
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "20px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "850px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 28px",
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#0f172a" }}>
              Doctor Application Details
            </h2>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              ID: {application._id}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px 28px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {/* Top Banner Card */}
          <div
            style={{
              padding: "16px 20px",
              borderRadius: "12px",
              backgroundColor: "#f1f5f9",
              border: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>
                {fullName}
              </h3>
              <p style={{ margin: "4px 0 0", fontSize: "14px", color: "#475569" }}>
                {email} {application.mobileNumber ? `• ${application.mobileNumber}` : ""}
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {application.isMobileVerified && (
                <span
                  style={{
                    padding: "4px 10px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    backgroundColor: "#dbeafe",
                    color: "#1e40af",
                  }}
                >
                  ✓ Mobile Verified
                </span>
              )}
              <span
                style={{
                  padding: "4px 12px",
                  borderRadius: "999px",
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  backgroundColor: status.includes("APPROV")
                    ? "#d1fae5"
                    : status.includes("REJECT")
                    ? "#fee2e2"
                    : "#fef3c7",
                  color: status.includes("APPROV")
                    ? "#065f46"
                    : status.includes("REJECT")
                    ? "#991b1b"
                    : "#92400e",
                }}
              >
                {status.replace(/_/g, " ")}
              </span>
            </div>
          </div>

          {/* Section 1: Basic Profile Info */}
          <div>
            <h4
              style={{
                margin: "0 0 12px",
                fontSize: "15px",
                fontWeight: 700,
                color: "#174350",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Basic Information
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "14px 20px",
                padding: "16px",
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
              }}
            >
              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  Gender
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.gender || "—"}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  Date of Birth
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {formatDate(application.dateOfBirth)}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  MBBS Type
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.mbbsType || "—"}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  Qualification / Specialization
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.degreeQualification ||
                    application.doctorType ||
                    application.specialization ||
                    "—"}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  Custom Qualification
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.customQualification || "—"}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  Institution Name
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.institutionName || "—"}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  Practice City
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.practiceCity || application.city || "—"}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  SMC Registration ID
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.smcRegistrationId || "—"}
                </strong>
              </div>

              <div>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block" }}>
                  NMC Registration ID
                </span>
                <strong style={{ fontSize: "14px", color: "#1e293b" }}>
                  {application.nmcRegistrationId || "—"}
                </strong>
              </div>
            </div>
          </div>

          {/* Section 2: Additional Degrees */}
          {application.additionalDegreesList &&
            application.additionalDegreesList.length > 0 && (
              <div>
                <h4
                  style={{
                    margin: "0 0 12px",
                    fontSize: "15px",
                    fontWeight: 700,
                    color: "#174350",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Additional Degrees
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {application.additionalDegreesList.map((deg, idx) => (
                    <div
                      key={deg.id || idx}
                      style={{
                        padding: "14px 16px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <span style={{ fontSize: "11px", color: "#64748b" }}>
                          Degree
                        </span>
                        <p style={{ margin: 0, fontSize: "13px", fontWeight: 600 }}>
                          {deg.degreeQualification || "—"}
                        </p>
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "#64748b" }}>
                          SMC ID
                        </span>
                        <p style={{ margin: 0, fontSize: "13px", fontWeight: 600 }}>
                          {deg.smcRegistrationId || "—"}
                        </p>
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "#64748b" }}>
                          NMC ID
                        </span>
                        <p style={{ margin: 0, fontSize: "13px", fontWeight: 600 }}>
                          {deg.nmcRegistrationId || "—"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Section 3: Uploaded Documents */}
          <div>
            <h4
              style={{
                margin: "0 0 12px",
                fontSize: "15px",
                fontWeight: 700,
                color: "#174350",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Uploaded Documents ({application.documentsList?.length || 0})
            </h4>

            {application.documentsList && application.documentsList.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "12px",
                }}
              >
                {application.documentsList.map((doc, idx) => {
                  const isImage =
                    doc.mimeType?.startsWith("image/") ||
                    doc.url?.match(/\.(jpeg|jpg|gif|png|webp)$/i);

                  return (
                    <div
                      key={doc.id || idx}
                      style={{
                        padding: "14px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: "10px",
                      }}
                    >
                      <div>
                        {isImage && doc.url && (
                          <div
                            style={{
                              width: "100%",
                              height: "120px",
                              marginBottom: "10px",
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: "1px solid #cbd5e1",
                              backgroundColor: "#f8fafc",
                            }}
                          >
                            <img
                              src={doc.url}
                              alt={doc.name || "Document"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}
                        <p
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#0f172a",
                            wordBreak: "break-all",
                          }}
                        >
                          📄 {doc.originalFileName || doc.name || "Document"}
                        </p>
                        <span style={{ fontSize: "11px", color: "#64748b" }}>
                          {formatFileSize(doc.size)}{" "}
                          {doc.uploadedAt ? `• ${formatDate(doc.uploadedAt)}` : ""}
                        </span>
                      </div>

                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "8px 12px",
                            backgroundColor: "#eff6ff",
                            color: "#1d4ed8",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: 600,
                            textDecoration: "none",
                            border: "1px solid #bfdbfe",
                          }}
                        >
                          👁️ View Document
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>
                No documents uploaded yet.
              </p>
            )}
          </div>

          {/* Previous Admin Feedback / Notes */}
          {(application.adminFeedback || application.adminNotes) && (
            <div
              style={{
                padding: "14px 16px",
                backgroundColor: "#fffbeb",
                border: "1px solid #fef3c7",
                borderRadius: "10px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#92400e",
                  textTransform: "uppercase",
                }}
              >
                Previous Admin Note / Feedback
              </span>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#78350f" }}>
                {application.adminFeedback || application.adminNotes}
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 28px",
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              color: "#334155",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Close
          </button>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={isRejecting || isApproving}
              style={{
                padding: "10px 22px",
                border: "none",
                borderRadius: "8px",
                backgroundColor: "#dc2626",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "14px",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(220, 38, 38, 0.2)",
              }}
            >
              {isRejecting ? "Rejecting..." : "Reject"}
            </button>

            <button
              onClick={handleApprove}
              disabled={isApproving || isRejecting}
              style={{
                padding: "10px 24px",
                border: "none",
                borderRadius: "8px",
                backgroundColor: "#059669",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "14px",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(5, 150, 105, 0.2)",
              }}
            >
              {isApproving ? "Approving..." : "Approve"}
            </button>
          </div>
        </div>
      </div>

      {/* Reject Reason Modal Prompt */}
      {showRejectModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2100,
            padding: "20px",
          }}
          onClick={() => setShowRejectModal(false)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "480px",
              padding: "24px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 12px",
                fontSize: "18px",
                fontWeight: 700,
                color: "#991b1b",
              }}
            >
              Reject Doctor Application
            </h3>
            <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#475569" }}>
              Please specify the reason for rejection to send as feedback to the doctor:
            </p>

            <form onSubmit={handleRejectSubmit}>
              <textarea
                rows={4}
                value={rejectFeedback}
                onChange={(e) => setRejectFeedback(e.target.value)}
                placeholder="Enter feedback/reason for rejection..."
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                required
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  margin: "12px 0 20px",
                }}
              >
                <input
                  type="checkbox"
                  id="reqReupload"
                  checked={requestReupload}
                  onChange={(e) => setRequestReupload(e.target.checked)}
                />
                <label
                  htmlFor="reqReupload"
                  style={{ fontSize: "13px", color: "#334155", cursor: "pointer" }}
                >
                  Request re-upload of document/details
                </label>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    backgroundColor: "#f1f5f9",
                    color: "#334155",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isRejecting}
                  style={{
                    padding: "8px 20px",
                    border: "none",
                    borderRadius: "6px",
                    backgroundColor: "#dc2626",
                    color: "#ffffff",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  {isRejecting ? "Submitting..." : "Confirm Rejection"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDoctorApplicationModal;
